// Svelte use: action for 3D tilt effect on mouse move
// Ported from public/zoom.js tilt tracking (lines 740-881)

const MAX_TILT_DEGREES = 7.5;
const PERSPECTIVE_PX = 1000;

// Track global mouse position so we can apply initial tilt without jerk
let lastMouseX = typeof window !== 'undefined' ? window.innerWidth / 2 : 0;
let lastMouseY = typeof window !== 'undefined' ? window.innerHeight / 2 : 0;

if (typeof window !== 'undefined') {
  window.addEventListener(
    'mousemove',
    (e) => {
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
    },
    { passive: true }
  );
}

/**
 * Calculate 3D tilt rotation angles based on mouse position relative to viewport.
 * @param {number} mouseX - Mouse X coordinate in pixels
 * @param {number} mouseY - Mouse Y coordinate in pixels
 * @returns {{ tiltRotateX: number, tiltRotateY: number }}
 */
function calculateTiltAngles(mouseX, mouseY) {
  const xPercent = mouseX / window.innerWidth;
  const yPercent = mouseY / window.innerHeight;
  return {
    tiltRotateY: (xPercent - 0.5) * MAX_TILT_DEGREES * 2,
    tiltRotateX: -(yPercent - 0.5) * MAX_TILT_DEGREES * 2,
  };
}

/**
 * Apply tilt transform to an element from the given angles.
 */
function applyTilt(node, angles) {
  node.style.transform = `perspective(${PERSPECTIVE_PX}px) rotateX(${angles.tiltRotateX}deg) rotateY(${angles.tiltRotateY}deg)`;
}

/**
 * Svelte action: applies 3D tilt effect to an element based on mouse position.
 * Desktop only — does nothing on touch devices.
 *
 * @param {HTMLElement} node - The element to apply tilt to
 * @param {{ enabled?: boolean }} options
 */
export function tilt(node, options = {}) {
  let { enabled = false } = options;

  function handleMouseMove(e) {
    if (!enabled) return;
    applyTilt(node, calculateTiltAngles(e.clientX, e.clientY));
  }

  function addListener() {
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
  }

  function removeListener() {
    window.removeEventListener('mousemove', handleMouseMove);
  }

  if (enabled) {
    addListener();
    // Apply initial tilt from last known mouse position (no jerk)
    applyTilt(node, calculateTiltAngles(lastMouseX, lastMouseY));
  }

  return {
    update(newOptions) {
      const wasEnabled = enabled;
      enabled = newOptions.enabled ?? false;
      if (enabled && !wasEnabled) {
        addListener();
        // Apply initial tilt from last known mouse position (no jerk)
        applyTilt(node, calculateTiltAngles(lastMouseX, lastMouseY));
      }
      if (!enabled && wasEnabled) {
        removeListener();
        // Reset to centered position without tilt
        node.style.transform = '';
      }
    },
    destroy() {
      removeListener();
    },
  };
}
